import { Log, UserManager, WebStorageStateStore } from 'oidc-client-ts';

import type { AuthConfig } from './types';

/**
 * Creates OIDC Configuration for Zitadel
 *
 * Uses Authorization Code flow with PKCE (Proof Key for Code Exchange)
 * which is the recommended flow for SPAs and native apps.
 *
 * NOTE: This function must only be called on the client side (browser)
 * because it uses WebStorageStateStore which requires localStorage.
 */
function createOidcConfig(config: AuthConfig) {
  // Enable debug logging in development (only in browser)
  if (config.debug) {
    Log.setLogger(console);
    Log.setLevel(Log.DEBUG);
  }

  const callbackPath = config.callbackPath || '/auth/callback';

  return {
    // Zitadel instance URL
    authority: config.authority,

    // Client ID from Zitadel Console (OIDC Application)
    client_id: config.clientId,

    // Where to redirect after login
    redirect_uri: `${config.appUrl}${callbackPath}`,

    // Where to redirect after logout
    post_logout_redirect_uri: config.appUrl,

    // Response type for Authorization Code + PKCE flow
    response_type: 'code',

    // Scopes requested from Zitadel
    // - openid: Required for OIDC
    // - profile: Basic profile info (name, etc.)
    // - email: Email address
    // - offline_access: Enables refresh tokens for session persistence
    scope: 'openid profile email offline_access',

    // Use localStorage for persistent sessions across browser restarts
    userStore: new WebStorageStateStore({
      store: window.localStorage,
    }),

    // Automatically include user info in token response
    loadUserInfo: true,

    // Silent token renewal settings
    automaticSilentRenew: true,
    silentRequestTimeoutInSeconds: 10,

    // Token handling
    filterProtocolClaims: true,
    revokeTokensOnSignout: true,
  };
}

/**
 * Cached UserManager instances per config
 */
const userManagerCache = new Map<string, UserManager>();

/**
 * Get or create a UserManager instance for the given config
 *
 * Usage:
 * - userManager.signinRedirect() - Start login flow
 * - userManager.signinRedirectCallback() - Handle callback after login
 * - userManager.signoutRedirect() - Start logout flow
 * - userManager.getUser() - Get current user from storage
 * - userManager.removeUser() - Clear user from storage
 */
export function createUserManager(config: AuthConfig): UserManager {
  if (typeof window === 'undefined') {
    throw new Error('UserManager can only be used in browser environment');
  }

  // Create a cache key from config
  const cacheKey = `${config.authority}:${config.clientId}`;

  let userManager = userManagerCache.get(cacheKey);
  if (userManager) {
    return userManager;
  }

  if (!config.clientId) {
    console.warn(
      'Zitadel client ID not configured. Set VITE_ZITADEL_CLIENT_ID env var.',
    );
  }

  const oidcConfig = createOidcConfig(config);
  userManager = new UserManager(oidcConfig);

  // Listen for token expiration events
  userManager.events.addAccessTokenExpiring(() => {
    console.log('[Auth] Access token expiring, attempting silent renewal...');
  });

  userManager.events.addAccessTokenExpired(() => {
    console.log('[Auth] Access token expired');
  });

  userManager.events.addSilentRenewError((error) => {
    console.error('[Auth] Silent renew error:', error);
  });

  userManager.events.addUserLoaded((user) => {
    console.log('[Auth] User loaded:', user.profile.sub);
  });

  userManager.events.addUserUnloaded(() => {
    console.log('[Auth] User unloaded');
  });

  userManagerCache.set(cacheKey, userManager);
  return userManager;
}

/**
 * Clear the UserManager cache (useful for testing)
 */
export function clearUserManagerCache(): void {
  userManagerCache.clear();
}
